import { useParams, useNavigate } from 'react-router-dom';
import { useOdoo } from '../../context/OdooContext.js';
import { useCctpProject } from '../../hooks/useCctpProject.js';
import { useMdd, LOT_ICONS } from '../../hooks/useMdd.js';

function completionPct(lotData: Record<string, unknown> | undefined, fieldCount: number): number {
  if (!lotData || fieldCount === 0) return 0;
  const filled = Object.values(lotData).filter(v => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)).length;
  return Math.min(100, Math.round((filled / fieldCount) * 100));
}

export default function LotsTableScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { client } = useOdoo();
  const { project, lots, loading } = useCctpProject(client, id ? parseInt(id) : null);
  const mdd = useMdd();

  if (loading) return <div className="min-h-screen bg-bg flex items-center justify-center text-ink-3">Chargement…</div>;
  if (!project) return null;

  const totalFields = mdd.lots.reduce((acc, l) => acc + l.sub_lots.reduce((a, s) => a + s.fields.length, 0), 0);
  const filledFields = mdd.lots.reduce((acc, l) => {
    const ld = lots[l.code] as Record<string, unknown> | undefined;
    if (!ld) return acc;
    return acc + Object.values(ld).filter(v => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)).length;
  }, 0);
  const globalPct = totalFields ? Math.round((filledFields / totalFields) * 100) : 0;

  return (
    <div className="min-h-screen bg-bg">
      <header className="bg-white border-b border-line px-8 py-4 flex items-center gap-4">
        <button onClick={() => navigate(`/projects/${id}`)} className="text-ink-3 hover:text-ink text-sm transition-colors">
          ← {project.name}
        </button>
        <div className="h-4 w-px bg-line" />
        <h1 className="font-bold text-ink">12 Lots</h1>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-sm text-ink-3">{globalPct}% complété</div>
          <div className="w-32 h-1.5 bg-line rounded-full overflow-hidden">
            <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${globalPct}%` }} />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-8">
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line">
                <th className="text-left text-xs font-semibold text-ink-3 px-6 py-3 w-8">#</th>
                <th className="text-left text-xs font-semibold text-ink-3 px-4 py-3">Lot</th>
                <th className="text-left text-xs font-semibold text-ink-3 px-4 py-3">Sous-lots</th>
                <th className="text-left text-xs font-semibold text-ink-3 px-4 py-3">Progression</th>
                <th className="text-left text-xs font-semibold text-ink-3 px-4 py-3">Estimation</th>
                <th className="w-10 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {mdd.lots.map(lot => {
                const ld = lots[lot.code] as Record<string, unknown> | undefined;
                const fieldCount = lot.sub_lots.reduce((a, s) => a + s.fields.length, 0);
                const pct = completionPct(ld, fieldCount);
                return (
                  <tr
                    key={lot.code}
                    onClick={() => navigate(`/projects/${id}/lots/${lot.code}`)}
                    className="border-b border-line last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-xl">{LOT_ICONS[lot.code]}</td>
                    <td className="px-4 py-4">
                      <div className="text-xs text-ink-3 mb-0.5">Lot {lot.num}</div>
                      <div className="text-sm font-semibold text-ink">{lot.label}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-ink-2">{lot.sub_lots_count}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-line rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green' : pct > 0 ? 'bg-brand' : 'bg-line'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-ink-3 w-8">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-ink-2">
                      {lot.estimation_default ? `${lot.estimation_default.toLocaleString('fr-FR')} €` : '—'}
                    </td>
                    <td className="px-4 py-4 text-ink-3">→</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
